import { z } from 'zod';

import { INSTANCES } from './instance';
import { SESSION_END_DISPOSITIONS } from './session';

/**
 * The session board (APP-MONITORING-REQUIREMENTS §14, accepted by the owner
 * 2026-09-13) — the contract between apps/api's two `/api/ops/board/*` routes
 * and apps/ops-web, the operators' phone-installable page.
 *
 * WHAT MAY TRAVEL, AND WHAT NEVER DOES (SB.9; ADR-0025 §4). A session is named
 * by its id; a patient only by study code or, where none was minted, episode
 * id; a physiotherapist by the registry display name; a clinic by its name.
 * No schema below has a field for a patient's name, contact detail, free text
 * or any clinical measurement, and every object is STRICT: the API parses its
 * own answer through these schemas before sending it, so a field added on the
 * server without a line here fails there instead of reaching a phone.
 *
 * READ-ONLY (SB.7). Nothing here describes an action. The board resumes, ends,
 * closes, moves and reassigns nothing.
 */

/**
 * What a session in motion is doing (§14.1, SB.1). The order of this tuple is
 * the board's precedence and its sort order.
 *
 *  - `recovering` — `dropped`; resumable until the technical close.
 *  - `physio-app-silent` — `live`; a heartbeat from the current live period is
 *    on record and older than the heartbeat grace.
 *  - `patient-reports-physio-gone` — `live`; the patient's browser reported
 *    the physiotherapist's video gone during the current live period.
 *  - `live-patient-joined` / `live-patient-not-in` — `live`; a
 *    `session.joined` during the current live period, or none.
 *  - `patient-waiting` / `link-sent` — `pending` with an unexpired link; the
 *    link has been opened (`link.resolved`), or not.
 *
 * The record cannot see a patient leave, so no state says "in call".
 */
export const OPS_BOARD_STATES = [
  'recovering',
  'physio-app-silent',
  'patient-reports-physio-gone',
  'live-patient-joined',
  'live-patient-not-in',
  'patient-waiting',
  'link-sent',
] as const;
export type OpsBoardState = (typeof OPS_BOARD_STATES)[number];

/** The drop sweeper's verdict (ADR-0032 SC5), read off the event it wrote. */
export const OPS_BOARD_VERDICTS = ['partial', 'did-not-happen'] as const;
export type OpsBoardVerdict = (typeof OPS_BOARD_VERDICTS)[number];

/** How a patient is referred to (ADR-0025 §4) — never by name. */
export const OPS_BOARD_PATIENT_REF_KINDS = ['study-code', 'episode-id', 'none'] as const;
export type OpsBoardPatientRefKind = (typeof OPS_BOARD_PATIENT_REF_KINDS)[number];

const count = z.number().int().nonnegative();
const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
const fingerprint = z.string().regex(/^[0-9a-f]{64}$/);

/** One session in motion — §14.1's row. */
export const opsBoardRowSchema = z.strictObject({
  sessionId: z.uuid(),
  state: z.enum(OPS_BOARD_STATES),
  clinicName: z.string().min(1).nullable(),
  physioName: z.string().min(1).nullable(),
  patientRef: z.string().min(1).nullable(),
  patientRefKind: z.enum(OPS_BOARD_PATIENT_REF_KINDS),
  /** The programme day of the slot the session was opened for; null = ad-hoc. */
  programmeDay: z.number().int().positive().nullable(),
  /** When the current state event says the session entered its status. */
  stateSince: z.iso.datetime(),
  /**
   * A heartbeat from the current live period is on record. False for a
   * session whose app build predates ADR-0032 or whose call has not been
   * joined yet — such a session is never shown as silent.
   */
  heartbeatOnRecord: z.boolean(),
  lastHeartbeatAt: z.iso.datetime().nullable(),
  /** The first `session.joined` of the current live period. */
  joinedAt: z.iso.datetime().nullable(),
  /** `recoveryDeadline` while it is set — the two middle states only. */
  countdownEndsAt: z.iso.datetime().nullable(),
  /** `dropped` only: the drop's receive time plus the technical-close grace. */
  resumableUntil: z.iso.datetime().nullable(),
  /** `dropped` only: the verdict the sweeper wrote on the drop. */
  verdict: z.enum(OPS_BOARD_VERDICTS).nullable(),
});
export type OpsBoardRow = z.infer<typeof opsBoardRowSchema>;

/**
 * One line of §14.3's follow-up lists. Which fields are set depends on the
 * list; `at` is the moment the list is about:
 *
 *  - `recovering` — the drop's receive time; `verdict`.
 *  - `technicalCloseAwaitingSignOff` — the technical close's receive time; `verdict`.
 *  - `neverJoinedToday` — the session's first `live` transition.
 *  - `awaitingSignOffOver24h` — the end's receive time; `disposition`.
 *  - `stuckLive` — the latest `live` transition's receive time.
 *  - `uploadsNotConfirmed` — the oldest unconfirmed part's creation; `pendingParts`.
 *  - `dueTodayNoSession` — the start of today (IST); `episodeId`,
 *    `programmeDay`, `coverActive`, `ownerActive`, and `physioName` is the
 *    episode's owner.
 */
export const opsBoardFollowUpItemSchema = z.strictObject({
  sessionId: z.uuid().nullable(),
  episodeId: z.uuid().nullable(),
  clinicName: z.string().min(1).nullable(),
  physioName: z.string().min(1).nullable(),
  patientRef: z.string().min(1).nullable(),
  patientRefKind: z.enum(OPS_BOARD_PATIENT_REF_KINDS),
  at: z.iso.datetime(),
  verdict: z.enum(OPS_BOARD_VERDICTS).nullable(),
  disposition: z.enum(SESSION_END_DISPOSITIONS).nullable(),
  programmeDay: z.number().int().positive().nullable(),
  pendingParts: count.nullable(),
  /** A live covering grant exists (ADR-0028) — read off `clinicRoster`. */
  coverActive: z.boolean().nullable(),
  /** The owner is still an active member of the clinic (ADR-0028 §7). */
  ownerActive: z.boolean().nullable(),
});
export type OpsBoardFollowUpItem = z.infer<typeof opsBoardFollowUpItemSchema>;

export const OPS_BOARD_FOLLOW_UP_LISTS = [
  'recovering',
  'technicalCloseAwaitingSignOff',
  'neverJoinedToday',
  'awaitingSignOffOver24h',
  'stuckLive',
  'uploadsNotConfirmed',
  'dueTodayNoSession',
] as const;
export type OpsBoardFollowUpList = (typeof OPS_BOARD_FOLLOW_UP_LISTS)[number];

const followUpItems = z.array(opsBoardFollowUpItemSchema);

export const opsBoardFollowUpSchema = z.strictObject({
  recovering: followUpItems,
  technicalCloseAwaitingSignOff: followUpItems,
  neverJoinedToday: followUpItems,
  awaitingSignOffOver24h: followUpItems,
  stuckLive: followUpItems,
  uploadsNotConfirmed: followUpItems,
  dueTodayNoSession: followUpItems,
});
export type OpsBoardFollowUp = z.infer<typeof opsBoardFollowUpSchema>;

/**
 * §14.2's numbers for one range and one grouping. The slot numbers
 * (`planned`, `started`, `missed`) count dated slots whose day falls in the
 * range, up to today; every other number counts sessions created in the range.
 * Days are IST days.
 */
export const opsBoardNumbersSchema = z.strictObject({
  planned: count,
  /** A planned day a session was opened for — what the clinic console calls *done*. */
  started: count,
  missed: count,
  adHoc: count,
  completed: count,
  stoppedEarlySafety: count,
  technicalPartial: count,
  technicalDidNotHappen: count,
  /** Ended sessions the patient never got into. */
  neverJoined: count,
  signedOff: count,
  awaitingSignOff: count,
  sessionsStarted: count,
  patientsJoined: count,
  /** Median seconds from a link's first opening to the patient's first join. */
  medianSecondsToJoin: z.number().nonnegative().nullable(),
  resumedAfterDrop: count,
});
export type OpsBoardNumbers = z.infer<typeof opsBoardNumbersSchema>;

export const opsBoardGroupSchema = z.strictObject({
  /** Null = no clinic on record, or no registry display name. */
  name: z.string().min(1).nullable(),
  numbers: opsBoardNumbersSchema,
});
export type OpsBoardGroup = z.infer<typeof opsBoardGroupSchema>;

export const opsBoardRangeSchema = z.strictObject({
  from: isoDate,
  to: isoDate,
  total: opsBoardNumbersSchema,
  byClinic: z.array(opsBoardGroupSchema),
  byPhysio: z.array(opsBoardGroupSchema),
});
export type OpsBoardRange = z.infer<typeof opsBoardRangeSchema>;

export const OPS_BOARD_RANGES = ['today', 'last7', 'last30'] as const;
export type OpsBoardRangeKey = (typeof OPS_BOARD_RANGES)[number];

export const opsBoardCountsSchema = z.strictObject({
  live: count,
  dropped: count,
  waiting: count,
  followUp: z.strictObject({
    recovering: count,
    technicalCloseAwaitingSignOff: count,
    neverJoinedToday: count,
    awaitingSignOffOver24h: count,
    stuckLive: count,
    uploadsNotConfirmed: count,
    dueTodayNoSession: count,
  }),
});
export type OpsBoardCounts = z.infer<typeof opsBoardCountsSchema>;

/**
 * `GET /api/ops/board/summary` — aggregate only, never audited (ADR-0025 §5),
 * polled every 30 s while the page is visible. `fingerprint` changes whenever
 * what the sessions route would show changes, which is the page's only reason
 * to ask for the rows again (SB.3).
 */
export const opsBoardSummaryResponseSchema = z.strictObject({
  ok: z.literal(true),
  asOf: z.iso.datetime(),
  instance: z.enum(INSTANCES),
  label: z.string().min(1),
  fingerprint,
  counts: opsBoardCountsSchema,
  numbers: z.strictObject({
    today: opsBoardRangeSchema,
    last7: opsBoardRangeSchema,
    last30: opsBoardRangeSchema,
  }),
  rules: z.strictObject({
    heartbeatGraceSeconds: count,
    armedMinutes: count,
    technicalCloseMinutes: count,
  }),
});
export type OpsBoardSummaryResponse = z.infer<typeof opsBoardSummaryResponseSchema>;

/**
 * `GET /api/ops/board/sessions` — rows keyed to sessions and episodes, so each
 * request writes one `ops.sessions.read` audit row (M5.9).
 */
export const opsBoardSessionsResponseSchema = z.strictObject({
  ok: z.literal(true),
  asOf: z.iso.datetime(),
  instance: z.enum(INSTANCES),
  label: z.string().min(1),
  fingerprint,
  rows: z.array(opsBoardRowSchema),
  followUp: opsBoardFollowUpSchema,
});
export type OpsBoardSessionsResponse = z.infer<typeof opsBoardSessionsResponseSchema>;
